/**
 * Auth token management for the website.
 */

const KEY_TOKEN = 'map_site_token';
const KEY_REFRESH = 'map_site_refresh';
const KEY_USER = 'map_site_user';
const KEY_REMEMBER = 'map_site_remember';

// One-time migration from old customer-portal keys
const OLD_KEYS = {
  token: 'map_customer_token',
  refresh: 'map_customer_refresh',
  user: 'map_customer_user',
  remember: 'map_customer_remember',
};
if (typeof window !== 'undefined' && !localStorage.getItem(KEY_TOKEN) && localStorage.getItem(OLD_KEYS.token)) {
  for (const store of [localStorage, sessionStorage]) {
    for (const [newK, oldK] of [
      [KEY_TOKEN, OLD_KEYS.token],
      [KEY_REFRESH, OLD_KEYS.refresh],
      [KEY_USER, OLD_KEYS.user],
      [KEY_REMEMBER, OLD_KEYS.remember],
    ] as const) {
      const v = store.getItem(oldK);
      if (v) { store.setItem(newK, v); store.removeItem(oldK); }
    }
  }
}

export interface SiteUser {
  sub: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  tenantId: string;
  role: 'customer';
  exp?: number;
  iat?: number;
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  customerId: string;
}

function getStore(): Storage {
  const remember = localStorage.getItem(KEY_REMEMBER);
  return remember === 'false' ? sessionStorage : localStorage;
}

export function getToken(): string | null {
  return sessionStorage.getItem(KEY_TOKEN) ?? localStorage.getItem(KEY_TOKEN);
}

export function getRefreshToken(): string | null {
  return sessionStorage.getItem(KEY_REFRESH) ?? localStorage.getItem(KEY_REFRESH);
}

export function setTokenPair(pair: TokenPair, rememberMe = true): void {
  localStorage.setItem(KEY_REMEMBER, rememberMe ? 'true' : 'false');
  const store = rememberMe ? localStorage : sessionStorage;
  const oppositeStore = rememberMe ? sessionStorage : localStorage;
  oppositeStore.removeItem(KEY_TOKEN);
  oppositeStore.removeItem(KEY_REFRESH);
  oppositeStore.removeItem(KEY_USER);
  store.setItem(KEY_TOKEN, pair.accessToken);
  store.setItem(KEY_REFRESH, pair.refreshToken);
  const user = parseJwt(pair.accessToken);
  if (user) store.setItem(KEY_USER, JSON.stringify(user));
}

export function clearAuth(): void {
  [localStorage, sessionStorage].forEach((s) => {
    s.removeItem(KEY_TOKEN);
    s.removeItem(KEY_REFRESH);
    s.removeItem(KEY_USER);
  });
}

export function getCurrentUser(): SiteUser | null {
  const raw = sessionStorage.getItem(KEY_USER) ?? localStorage.getItem(KEY_USER);
  if (raw) {
    try {
      return JSON.parse(raw) as SiteUser;
    } catch {
      // fall through
    }
  }
  const token = getToken();
  if (!token) return null;
  return parseJwt(token);
}

export function isTokenExpired(): boolean {
  const user = getCurrentUser();
  if (!user?.exp) return true;
  return user.exp * 1000 < Date.now() + 30_000;
}

export function getRememberMe(): boolean {
  return localStorage.getItem(KEY_REMEMBER) !== 'false';
}

function parseJwt(token: string): SiteUser | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as SiteUser;
  } catch {
    return null;
  }
}
